import React, { useCallback, useMemo, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import classname from 'classnames';
import type { PapiDeviation } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import {
  ThemeOptions,
  ThemeOverride,
} from '@wix/da-react-context/pkg/ThemeContext';
import {
  BiContextProvidedValue,
  BiData,
  GiveBadgeAwardBiEvent,
  WatchItemRemoveClickBiEvent,
  MoreLikeThisClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import BiButton from '@wix/da-bi/pkg/Components/BiButton';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import GiveBadge from '@wix/da-ds/pkg/Icons/16x16/GiveBadge';
import Ellipsis from '@wix/da-ds/pkg/Icons/Ellipsis';
import CloseIcon24 from '@wix/da-ds/pkg/Icons/24x24/Close';
import MltSearch24 from '@wix/da-ds/pkg/Icons/24x24/MltSearch';
import Boost24 from '@wix/da-ds/pkg/Icons/24x24/Boost';
import BoostFilled24 from '@wix/da-ds/pkg/Icons/24x24/BoostFilled';
import { BiContext } from '@wix/da-bi/pkg/BiLogger.context';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import Check from '@wix/da-ds/pkg/Icons/16x16/Check';
import IconButtonWithBackground from '@wix/da-ds/pkg/Buttons/IconButton/IconButtonWithBackground';
import HoverOnly from '@wix/da-ds/pkg/thumbs/OverlayHelpers/HoverOnly';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import type { NullableDropdownItem } from '@wix/da-ds/pkg/Dropdown/types';
import { useOverlayContext } from '../../../hooks/useOverlayContext';
import { useRestrictToLoggedIn } from '../../../../utils/hooks/useRestrictToLoggedIn';
import UserAvatar from '../../../../User/Avatar/UserAvatarWithPopover';
import UserLink from '../../../../User/Link/UserLinkWithTooltip';
import {
  getDeviationResourceType,
  getDeviationTitle,
  getIsPost,
} from '../../../_helpers';
import { DeviationResourceType } from '../../../../types/deviation';
import ThumbActions from '../../../Helper/ThumbActions';
import { useWatchButton } from '../../../../WatchButton/useWatchButton';
import { useDeviationBlockType } from '../../hooks/useDeviationBlockType';
import { useDeviationAttributes } from '../../../hooks/useDeviationAttributes';
import DeviationLink from '../../../../Duperbrowse/DeviationLink';
import FaveControl from '../FaveControl';
import CommentControl from '../CommentControl';
import { getDeviationOwner } from '../../../../utils/adoptables';
import BasicUserName from '../../../../User/BasicUserName/BasicUserName';
import { useSubscriptionTier } from '../../../../utils/hooks/useSubscriptionTier';
import { useIsCurrentUserDeviationAuthor } from '../../../../utils/hooks/useIsCurrentUserDeviationAuthor';
import { beginDeviationAwardBadgeFlow } from '../../../../coreBadge/redux/actionCreators';
import { AwardBadgeSource } from '../../../../coreBadge/types';
import BoostIcon from '@wix/da-ds/pkg/Icons/16x16/BoostFilled';
import BoostRootModal from '../../../../ActionBar/buttons/BoostButton/modals/BoostRootModal';
import s from './ThumbOverlayHover.scss';

export interface Props {
  deviation: PapiDeviation;
  width?: number;
  height?: number;
  className?: string;
  extraActions?: NullableDropdownItem[];
  onRemove?: () => void;
}

const ThumbOverlayHover: React.FC<React.PropsWithChildren<Props>> = ({
  deviation,
  width,
  height,
  children,
  className,
  extraActions,
  onRemove,
}) => {
  const [showBoostModal, setShowBoostModal] = useState(false);
  const {
    showTitle,
    showAuthor,
    showOwner,
    showCommentControl,
    showAuthorTooltip,
    showOwnerTooltip,
    showFaveControl,
    showExtraActions,
    showAuthorName,
    showOwnerName,
    showAuthorWatchButton,
    showOwnerWatchButton,
    showMlt,
    showAwardBadge,
    showBoosted,
  } = useOverlayContext(deviation);
  const { t } = useTranslation();
  const isAuthor = useIsCurrentUserDeviationAuthor(deviation);
  const { restrictToLoggedIn } = useRestrictToLoggedIn();
  const { needsSubscription, isSubscribedButBlocked } =
    useSubscriptionTier(deviation);
  const owner = getDeviationOwner(deviation);
  const { isWatching, text } = useWatchButton(deviation.author);
  const { isWatching: isWatchingOwner, text: ownerWatchText } =
    useWatchButton(owner);
  const blockType = useDeviationBlockType(deviation);
  const { isCoreBadgeAwardable, isBoosted } = useDeviationAttributes(
    deviation,
    isAuthor
  );
  const showSmallerActions = !!height && height < 150;

  const resourceType = getDeviationResourceType(deviation);
  const isMlt =
    resourceType === DeviationResourceType.ART &&
    !getIsPost(deviation) &&
    (!blockType || blockType === 'matureFilter') &&
    showMlt;
  const biContext = useContext(BiContext);
  const sectionname = biContext.sectionname;
  const dispatch = useDispatch();
  const handleAwardBadgeClick = useCallback(() => {
    dispatch(
      beginDeviationAwardBadgeFlow(
        deviation,
        AwardBadgeSource.THUMB_OVERLAY,
        sectionname
      )
    );
  }, [deviation, dispatch, sectionname]);

  const canShowTitle = useMemo(() => {
    if (!showTitle) {
      return false;
    }

    switch (blockType) {
      case 'subsOnly':
      case 'subBlocked':
        return height && height > 250;
      case 'premiumFolder':
        return height && height > 210;
      case 'watchersOnly':
        return height && height > 180;
      default:
        return true;
    }
  }, [blockType, height, showTitle]);

  const canAwardBadge =
    !isAuthor &&
    isCoreBadgeAwardable &&
    !(needsSubscription || isSubscribedButBlocked) &&
    showAwardBadge;

  const isBoostIconShowable = isAuthor;

  return (
    <ThemeOverride
      theme={ThemeOptions.DARK}
      className={classname(s['root'], className)}
      style={{ width, height }}
      tag="div"
    >
      <div className={s['top']}>
        <div className={s['left']} />
        <HoverOnly className={s['right']}>
          {showExtraActions && extraActions && (
            <SimpleTooltip
              text={t('deviationview.overlay.a11y.moreActions')}
              usePortal
              listenOnClicks={false}
            >
              <ThumbActions
                className={s['actions']}
                deviation={deviation}
                actions={extraActions}
              >
                <IconButtonWithBackground
                  size={showSmallerActions ? 'small' : 'medium'}
                  icon={Ellipsis}
                  aria-label={t('deviationview.overlay.a11y.moreActions')}
                />
              </ThumbActions>
            </SimpleTooltip>
          )}
          {onRemove && (
            <SimpleTooltip
              text={t('deviationview.overlay.a11y.remove')}
              usePortal
            >
              <IconButtonWithBackground
                className={s['remove-button']}
                size="medium"
                aria-label={t('deviationview.overlay.a11y.remove')}
                icon={CloseIcon24}
                onClick={onRemove}
                biData={BiData<WatchItemRemoveClickBiEvent>({
                  evid: 405,
                  typeid: getDeviationResourceType(deviation),
                  itemid: deviation.deviationId,
                  rank: BiContextProvidedValue,
                })}
              />
            </SimpleTooltip>
          )}
          {isMlt && (
            <SimpleTooltip text={t('common.moreLikeThis')} usePortal>
              <IconButtonWithBackground
                className={s['mlt-button']}
                iconClassName={s['mlt-icon']}
                size="medium"
                icon={MltSearch24}
                aria-label={t('common.moreLikeThis')}
                href={Url.moreLikeThisLink(deviation.deviationId)}
                biData={BiData<MoreLikeThisClickBiEvent>({
                  evid: 126,
                  typeid: getDeviationResourceType(deviation),
                  itemid: deviation.deviationId,
                  creatorid: deviation.author?.useridUuid ?? '',
                  sectionname,
                })}
              />
            </SimpleTooltip>
          )}
          {isBoostIconShowable && (
            <SimpleTooltip
              text={t(
                isBoosted ? 'actionbar.currentlyBoosted' : 'actionbar.boost'
              )}
              usePortal
            >
              <IconButtonWithBackground
                className={s['boost-button']}
                iconClassName={s['boost-icon']}
                size="medium"
                icon={isBoosted ? BoostFilled24 : Boost24}
                aria-label={t(
                  isBoosted ? 'actionbar.currentlyBoosted' : 'actionbar.boost'
                )}
                onClick={() => setShowBoostModal(true)}
                biData={BiData<GiveBadgeAwardBiEvent>({
                  evid: 320,
                  sectionname: BiContextProvidedValue,
                  itemid: deviation.deviationId,
                  typeid: getDeviationResourceType(deviation),
                  badge_type: 'boost',
                  badge_typeid: 176,
                })}
              />
            </SimpleTooltip>
          )}
        </HoverOnly>
      </div>
      <HoverOnly className={s['middle']}>{children}</HoverOnly>
      <div className={s['bottom']}>
        <HoverOnly className={s['left']}>
          {showBoosted && deviation.boosted && (
            <div className={s['boosted']}>
              <BoostIcon size={IconSize.SMALLER} disableDefaultColors />
              <span className={s['boosted-text']}>{t('common.boosted')}</span>
            </div>
          )}
          {canShowTitle && (
            <DeviationLink deviation={deviation} className={s['title']}>
              <Heading className={s['title-heading']}>
                {getDeviationTitle(deviation)}
              </Heading>
            </DeviationLink>
          )}
          {showAuthor && deviation.author && (
            <div className={s['author']}>
              <div className={s['avatar-and-username']}>
                <UserAvatar
                  user={deviation.author}
                  size={24}
                  withLink
                  withTooltip={showAuthorTooltip}
                  className={s['avatar']}
                />
                {showAuthorName && (
                  <UserLink
                    user={deviation.author}
                    withTooltip={showAuthorTooltip}
                    className={s['userlink']}
                  />
                )}
              </div>
              {isWatching && showAuthorWatchButton && (
                <TextButton size="small" variant="main" disabled icon={Check}>
                  {text}
                </TextButton>
              )}
            </div>
          )}
          {showOwner && owner && (
            <div className={s['author']}>
              <div className={s['avatar-and-username']}>
                <UserAvatar
                  user={owner}
                  size={24}
                  withLink
                  withTooltip={showOwnerTooltip}
                  className={s['avatar']}
                />
                {showOwnerName && (
                  <UserLink
                    user={owner}
                    withTooltip={showOwnerTooltip}
                    className={s['userlink']}
                  >
                    <BasicUserName user={owner} withOwnerText />
                  </UserLink>
                )}
              </div>
              {isWatchingOwner && showOwnerWatchButton && (
                <TextButton size="small" variant="main" disabled icon={Check}>
                  {ownerWatchText}
                </TextButton>
              )}
            </div>
          )}
        </HoverOnly>
        <HoverOnly className={classname(s['right'], s['stats'])}>
          {canAwardBadge && (
            <BiButton
              className={classname(s['stat'], s['award-button'])}
              onClick={restrictToLoggedIn(
                ReferralType.AWARD_BADGE,
                handleAwardBadgeClick
              )}
              aria-label={t('deviationview.overlay.a11y.awardBadge')}
              biData={BiData<GiveBadgeAwardBiEvent>({
                evid: 320,
                sectionname: BiContextProvidedValue,
                typeid: getDeviationResourceType(deviation),
                itemid: deviation.deviationId,
              })}
            >
              <GiveBadge
                className={s['award-icon']}
                disableDefaultColors
                size={IconSize.SMALLER}
              />
            </BiButton>
          )}
          {showCommentControl && (
            <CommentControl deviation={deviation} className={s['stat']} />
          )}
          {showFaveControl && (
            <FaveControl
              deviation={deviation}
              showStatNumbers
              className={s['stat']}
            />
          )}
        </HoverOnly>
      </div>
      {showBoostModal && (
        <BoostRootModal
          deviation={deviation}
          onRequestClose={() => setShowBoostModal(false)}
        />
      )}
    </ThemeOverride>
  );
};
ThumbOverlayHover.displayName = 'ThumbOverlayHover';

export default ThumbOverlayHover;
